import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TechStackIcons } from './TechStackIcons';

export const ProjectModal = ({
  isOpen,
  onClose,
  number,
  title,
  category,
  role,
  status,
  summary,
  features,
  tags,
  video,
  poster,
  images = [],
  onOpenLightbox,
  sourceUrl,
  theme,
}) => {
  const [showVideo, setShowVideo] = useState(!poster);
  const closeRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const titleId = `pf-case-${number}-title`;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    setShowVideo(!poster);
    closeRef.current?.focus();
    return () => previousFocus?.focus?.();
  }, [isOpen, poster]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="pf-modal-backdrop pf-case-backdrop"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.section
            className={`pf-case-modal pf-case-${theme}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={reduceMotion ? false : { opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 0.8, 0.24, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="pf-case-topbar">
              <span>{number} / {category}</span>
              <button ref={closeRef} className="pf-case-close" type="button" onClick={onClose} aria-label={`Close ${title}`}>
                <span aria-hidden="true">×</span>
              </button>
            </header>

            <div className="pf-case-layout">
              <div className="pf-case-media">
                <div className={`pf-case-stage${poster ? ' pf-case-stage-landscape' : ''}`}>
                  {showVideo ? (
                    <video controls autoPlay muted playsInline preload={poster ? 'none' : 'metadata'} aria-label={`${title} walkthrough video`}>
                      <source src={video} type="video/mp4" />
                    </video>
                  ) : (
                    <button className="pf-case-play" type="button" onClick={() => setShowVideo(true)} aria-label={`Play ${title} walkthrough`}>
                      <img src={poster} alt="" />
                      <span className="pf-case-play-icon" aria-hidden="true">▶</span>
                      <span className="pf-case-play-label">Play walkthrough</span>
                    </button>
                  )}
                </div>
                <div className="pf-case-media-meta"><span>{poster ? 'Project walkthrough' : 'App demonstration'}</span><span>{images.length ? `${images.length} screenshots` : 'Video preview'}</span></div>
                {images.length > 0 && (
                  <div className="pf-case-thumbnails" aria-label={`${title} screenshots`}>
                    {images.map((image, index) => (
                      <button key={image} type="button" onClick={() => onOpenLightbox(image)} aria-label={`View ${title} screen ${index + 1}`}>
                        <img src={image} alt="" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pf-case-content">
                <div className="pf-case-intro">
                  <span className="pf-case-category">Selected work / {category}</span>
                  <h3 id={titleId}>{title}</h3>
                  <p>{summary}</p>
                </div>
                <div className="pf-case-meta"><span>{role}</span><span>{status}</span></div>
                <div className="pf-case-details">
                  <h4>What it does</h4>
                  <ul>{features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                </div>
                <div className="pf-case-stack"><h4>Built with</h4><TechStackIcons tags={tags} /></div>
                <div className="pf-case-source">
                  {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer">View source on GitHub <span aria-hidden="true">↗</span></a> : <span>Source repository is private</span>}
                </div>
              </div>
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
